import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getAnalyticsData, AnalyticsData } from '../services/analyticsService';
import { useSubscription } from '../hooks/useSubscription';
import { useFirebase } from '../contexts/FirebaseContext';
import { useDashboardFilters } from '../contexts/DashboardFiltersContext';
import { useStoreLocation } from '../contexts/LocationContext';
import { TrendingUp, PieChart as PieIcon, Award, Lock } from 'lucide-react';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
}

const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#f43f5e', '#64748b'];

export const WeeklyTipsChart: React.FC<Props> = ({ tenantId }) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const sub = useSubscription(tenantId);
  const { dateRange, tipType } = useDashboardFilters();
  const { activeLocationId } = useStoreLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getAnalyticsData(tenantId, dateRange, tipType, activeLocationId);
        setData(result);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, dateRange, tipType, activeLocationId]);

  if (loading || sub.loading) {
    return (
      <div className="w-full h-[300px] bg-gray-50 rounded-3xl animate-pulse flex items-center justify-center">
        <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">Assembling analytics grid...</p>
      </div>
    );
  }

  const hasVelocity = sub.hasFeature('velocity');

  if (!data || (data.chartData.length === 0 && data.sourceData.length === 0)) {
    return null;
  }

  return (
    <div className="bg-gray-50/20 border border-gray-100 rounded-2xl md:rounded-[2rem] p-4 md:p-6 space-y-4">
      {/* Group Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-4">
        <div>
          <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">Activity Summary ({dateRange.replace(/_/g, ' ')})</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
        {/* Weekly Tip Velocity Bar Chart */}
        <div className="card shadow-2xl shadow-blue-500/5 overflow-hidden group relative">
          <div className="p-4 md:p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg md:rounded-xl">
                <TrendingUp size={16} className="md:w-[18px] md:h-[18px]" />
              </div>
              <div>
                <h3 className="text-xs md:text-sm font-black text-gray-900 tracking-tight uppercase">Daily Earning Velocity</h3>
                <p className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-widest">Shift performance distribution</p>
              </div>
            </div>
            {!hasVelocity && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg border border-amber-200">
                <Lock size={10} />
                <span className="text-[8px] font-black uppercase tracking-widest">Enhanced</span>
              </div>
            )}
          </div>
          
          <div className="p-4 md:p-8">
            <div className="h-[250px] w-full relative">
              {!hasVelocity ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center p-6 bg-white/60 backdrop-blur-[2px] rounded-2xl border border-dashed border-gray-200">
                  <TrendingUp size={32} className="text-gray-300 mb-2" />
                  <p className="text-xs font-black text-gray-900 uppercase tracking-tight italic">Velocity Insights Locked</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-1 leading-tight">Upgrade to Enhanced to track shift-level earning peaks.</p>
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 9, fontWeight: 900 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 9, fontWeight: 900 }}
                  />
                  <Tooltip 
                    cursor={{ fill: '#f8f9fa' }}
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      padding: '8px 12px'
                    }}
                    itemStyle={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase' }}
                    labelStyle={{ marginBottom: '2px', fontWeight: 900, color: '#111827' }}
                    formatter={(value: any) => [formatCurrency(Number(value) || 0)]}
                  />
                  <Legend 
                    verticalAlign="top" 
                    align="right" 
                    iconType="circle"
                    wrapperStyle={{ 
                        paddingBottom: '20px',
                        fontSize: '9px',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em'
                    }}
                  />
                  <Bar 
                    dataKey="Morning" 
                    fill="#3b82f6" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Bar 
                    dataKey="Evening" 
                    fill="#f59e0b" 
                    radius={[4, 4, 0, 0]} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tip Type Breakdown Donut Chart */}
        <div className="card shadow-2xl shadow-blue-500/5 overflow-hidden group">
          <div className="p-4 md:p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg md:rounded-xl">
                <PieIcon size={16} className="md:w-[18px] md:h-[18px]" />
              </div>
              <div>
                <h3 className="text-xs md:text-sm font-black text-gray-900 tracking-tight uppercase">Tip Type</h3>
                <p className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-widest">POS vs Cash</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 md:p-8 flex flex-col items-center justify-center">
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.sourceData}
                    cx="50%"
                    cy="45%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={1500}
                  >
                    {data.sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      padding: '8px 12px'
                    }}
                    itemStyle={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase' }}
                    formatter={(value: any) => [formatCurrency(Number(value) || 0)]}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    iconType="circle"
                    wrapperStyle={{ 
                        fontSize: '9px',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        paddingTop: '10px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Performers Section */}
        <div className="card shadow-2xl shadow-blue-500/5 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-emerald-100 text-emerald-600 rounded-lg md:rounded-xl">
                <Award size={16} className="md:w-[18px] md:h-[18px]" />
              </div>
              <div>
                <h3 className="text-xs md:text-sm font-black text-gray-900 tracking-tight uppercase">Top Performers</h3>
                <p className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-widest">Employee distribution</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 md:p-8">
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.employeeData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    animationBegin={200}
                    animationDuration={1500}
                  >
                    {data.employeeData.map((entry, index) => (
                      <Cell key={`cell-emp-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      padding: '8px 12px'
                    }}
                    itemStyle={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase' }}
                    formatter={(value: any) => [formatCurrency(Number(value) || 0)]}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    iconType="circle"
                    wrapperStyle={{ 
                        fontSize: '9px',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        paddingTop: '10px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
